import React from 'react';
import { Check, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  message: string;
  description?: string;
  type?: ToastType;
  show: boolean;
  onClose?: () => void;
  duration?: number;
  position?: 'top-center' | 'top-right' | 'bottom-center' | 'bottom-right';
}

const Toast: React.FC<ToastProps> = ({
  message,
  description,
  type = 'success',
  show,
  onClose,
  duration = 3000,
  position = 'bottom-center'
}) => {
  React.useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose]);

  if (!show) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          gradient: 'from-green-500 to-green-600',
          border: 'border-green-400',
          icon: <Check className="w-6 h-6 text-white" strokeWidth={3} />
        };
      case 'error':
        return {
          gradient: 'from-red-500 to-red-600',
          border: 'border-red-400',
          icon: <X className="w-6 h-6 text-white" strokeWidth={3} />
        };
      case 'warning':
        return {
          gradient: 'from-yellow-500 to-orange-500',
          border: 'border-yellow-400',
          icon: <AlertCircle className="w-6 h-6 text-white" strokeWidth={3} />
        };
      case 'info':
        return {
          gradient: 'from-blue-500 to-blue-600',
          border: 'border-blue-400',
          icon: <Info className="w-6 h-6 text-white" strokeWidth={3} />
        };
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top-center':
        return 'top-6 left-1/2 -translate-x-1/2';
      case 'top-right':
        return 'top-6 right-6';
      case 'bottom-center':
        return 'bottom-6 left-1/2 -translate-x-1/2';
      case 'bottom-right':
        return 'bottom-6 right-6';
    }
  };

  const getAnimationStyle = () => {
    if (position.startsWith('bottom')) {
      return {
        animation: 'slideUpFromBottom 0.5s ease-out'
      };
    } else {
      return {
        animation: 'slideDownFromTop 0.5s ease-out'
      };
    }
  };

  const styles = getToastStyles();
  const positionClass = getPositionStyles();

  return (
    <div 
      className={`fixed ${positionClass} bg-gradient-to-r ${styles.gradient} text-white px-6 py-4 rounded-2xl shadow-2xl z-[250] flex items-center gap-3 backdrop-blur-sm border ${styles.border} border-opacity-30 min-w-[280px] max-w-[90vw]`}
      style={getAnimationStyle()}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center justify-center w-10 h-10 bg-white bg-opacity-25 rounded-full flex-shrink-0">
        {styles.icon}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="font-semibold text-base leading-tight">{message}</span>
        {description && (
          <div className="text-sm opacity-95 truncate mt-1">{description}</div>
        )}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 ml-2 p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default Toast;
